import React, { createContext, useContext, useState } from 'react';
import {
  House, DEFAULT_DOMAIN, loadHouses, saveHouses, loadActiveHouseId, saveActiveHouseId,
  makeHouseId, renameHouseData, migrateLegacyAuth,
} from './houseStorage';

interface AuthContextValue {
  houses: House[];
  activeHouseId: string | null;
  isSetupDone: boolean;
  isAuthenticated: boolean;
  clientId: string;
  domain: string;
  username: string;
  addHouse: (clientId: string, domain: string, username: string, password: string) => void;
  switchHouse: (id: string) => void;
  goToHousePicker: () => void;
  login: (password: string) => void;
  restore: (clientId: string, domain: string, username: string) => void;
  logout: () => void;
  checkPassword: (pwd: string) => boolean;
  getAuthHeader: () => Record<string, string>;
  gatewayUrl: string;
}

const AuthContext = createContext<AuthContextValue>(null!);

export const useAuth = () => useContext(AuthContext);

interface HouseState {
  houses: House[];
  activeId: string | null;
}

const initHouseState = (): HouseState => {
  migrateLegacyAuth();
  const houses = loadHouses();
  let activeId = loadActiveHouseId();
  if (!activeId && houses.length === 1) {
    activeId = houses[0].id;
    saveActiveHouseId(activeId);
  }
  return { houses, activeId };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [{ houses, activeId }, setHouseState] = useState<HouseState>(initHouseState);
  const [password, setPassword] = useState<string | null>(null);

  const activeHouse = houses.find(h => h.id === activeId) ?? null;
  const isSetupDone = houses.length > 0;
  const isAuthenticated = activeHouse !== null && password !== null;
  const gatewayUrl = activeHouse ? `https://${activeHouse.clientId}${activeHouse.domain}` : '';

  const addHouse = (clientId: string, domain: string, username: string, pwd: string) => {
    const id = makeHouseId(clientId, domain);
    const house: House = { id, clientId, domain, username };
    // Upsert: replace an existing house with the same id in place instead of
    // appending a duplicate (e.g. re-adding a house to fix a typo'd username).
    // Two entries sharing an id would desync activeHouse lookups from the
    // password/state just set, and duplicate React keys in HousePicker.
    const existingIndex = houses.findIndex(h => h.id === id);
    const nextHouses = existingIndex >= 0
      ? houses.map((h, i) => i === existingIndex ? house : h)
      : [...houses, house];
    saveHouses(nextHouses);
    saveActiveHouseId(id);
    setHouseState({ houses: nextHouses, activeId: id });
    setPassword(pwd);
  };

  const switchHouse = (id: string) => {
    saveActiveHouseId(id);
    setHouseState(s => ({ ...s, activeId: id }));
    setPassword(null);
  };

  const goToHousePicker = () => {
    setPassword(null);
    saveActiveHouseId(null);
    setHouseState(s => ({ ...s, activeId: null }));
  };

  const login = (pwd: string) => {
    setPassword(pwd);
  };

  const restore = (clientId: string, domain: string, username: string) => {
    if (!activeHouse) return;
    const newId = makeHouseId(clientId, domain);
    const updatedHouse: House = { id: newId, clientId, domain, username };
    // Rename the active house in place, then drop any OTHER pre-existing
    // house that happens to already have this id -- otherwise renaming onto
    // a collision would leave two array entries sharing an id (same failure
    // mode as addHouse: stale entry wins activeHouse lookups).
    const nextHouses = houses
      .map(h => h.id === activeHouse.id ? updatedHouse : h)
      .filter(h => h === updatedHouse || h.id !== newId);
    if (newId !== activeHouse.id) renameHouseData(activeHouse.id, newId);
    saveHouses(nextHouses);
    saveActiveHouseId(newId);
    setHouseState({ houses: nextHouses, activeId: newId });
    setPassword(null);
  };

  const logout = () => {
    setPassword(null);
    if (houses.length > 1) {
      saveActiveHouseId(null);
      setHouseState(s => ({ ...s, activeId: null }));
    }
  };

  const checkPassword = (pwd: string): boolean => pwd === password;

  const getAuthHeader = (): Record<string, string> => {
    if (!activeHouse || !password) return {};
    return { Authorization: 'Basic ' + btoa(`${activeHouse.username}:${password}`) };
  };

  return (
    <AuthContext.Provider value={{
      houses,
      activeHouseId: activeId,
      isSetupDone,
      isAuthenticated,
      clientId: activeHouse?.clientId ?? '',
      domain: activeHouse?.domain ?? DEFAULT_DOMAIN,
      username: activeHouse?.username ?? '',
      addHouse,
      switchHouse,
      goToHousePicker,
      login,
      restore,
      logout,
      checkPassword,
      getAuthHeader,
      gatewayUrl,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
